import { useWatch } from '@vtex/admin-ui-form'
import { useProductsCount } from 'hooks'
import { useMemo } from 'react'
import { Item, ListOperator, OPERATORS } from 'typings'
import { useFormStateContext } from 'ui'
import { useNumbers } from 'ui/components/utils/useNumbers'
import { Promotion } from '../../../typings'
import {
  buildGenericScope,
  buildSalesChannelScope,
  GENERIC_SCOPE_OPTIONS,
  GenericScope,
} from './genericScope'

type Props = {
  enabled: boolean
}

export type useScopeReturnType = {
  percentage: number | undefined
  formattedPercentage: string
  loading: boolean
}

export const DECIMAL_DIGITS = 2

const EMPTY_SEARCH = ['']

export function getItemsQuery(items: Item[], prefix: string): string {
  return items.map((item) => `${prefix}${item.id}`).join('&')
}

export function getQueryPrefix(
  filter: GENERIC_SCOPE_OPTIONS,
  operator: ListOperator
): string {
  return `${filter}${operator === OPERATORS.DIFFERENT ? '-' : ''}=`
}

export function getSearchQueries(
  scopes: Array<GenericScope> | undefined
): string[] {
  if (!scopes) return []

  return scopes.map((scope) => {
    return Object.values(GENERIC_SCOPE_OPTIONS)
      .reduce((queries, fieldName) => {
        const field = scope[fieldName]
        if (!field) return queries
        const { items, operator } = field

        if (!items.length) {
          return queries
        }

        return [
          ...queries,
          getItemsQuery(items, getQueryPrefix(fieldName, operator)),
        ]
      }, [])
      .join('&')
  })
}

/**
 * Calculates the percentage of active products affected by the current promotion.
 *
 * How it works:
 * - Watches the entire promotion form state and converts it into a generic scope.
 * - Translates the generic scope into one or multiple search query strings used by
 *   the products count API.
 * - Fetches, in parallel, the number of products matching the scope and the total
 *   number of products, then computes (matched/total)*100.
 *
 * Edge cases:
 * - When the promotion applies to all items (no scope), it returns 100.
 * - While counts are loading, returns percentage as undefined and loading=true.
 * - If the total is 0, returns 0 to avoid division by zero.
 *
 * @param {boolean} enabled toggles the underlying queries; defaults to true.
 *
 * @returns {number | undefined} percentage (undefined while loading)
 * @returns {string} formattedPercentage locale-formatted string using two digits precision
 * @returns {boolean} loading
 */
export function useProductsPercentage(props?: Props): useScopeReturnType {
  const enabled = props?.enabled ?? true

  const { form } = useFormStateContext()

  // @ts-expect-error useWatch supports name as undefined to watch the whole form,
  // although is not exposed by admin-ui-form.
  const promotion: Promotion = useWatch({ form, name: undefined })

  const genericScope = useMemo(() => buildGenericScope(promotion), [promotion])

  const searchQueries = useMemo(
    () => getSearchQueries(genericScope),
    [genericScope]
  )

  const { data: productsFound, isLoading: loadingSearch } = useProductsCount({
    searches: searchQueries,
    enabled: enabled && genericScope !== undefined,
  })

  const { data: allProducts, isLoading: loadingAllProducts } = useProductsCount(
    {
      searches: EMPTY_SEARCH,
      enabled: enabled && genericScope !== undefined,
    }
  )

  const { formatPercentual } = useNumbers()

  const percentage = useMemo(() => {
    if (!genericScope) return 100

    if (productsFound === undefined || allProducts === undefined)
      return undefined

    if (allProducts === 0) return 0

    return (productsFound * 100) / allProducts
  }, [allProducts, productsFound, genericScope])

  const formattedPercentage: string =
    percentage !== undefined ? formatPercentual(percentage, DECIMAL_DIGITS) : ''

  return {
    percentage,
    formattedPercentage,
    loading: loadingAllProducts || loadingSearch,
  }
}
